import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/api/client';
import { StatusPill } from '@/components/ui/status-pill';
import { FilterChip } from '@/components/ui/filter-chip';
import { cn, taxYearLabel } from '@/lib/utils';
import type {
  AgentNote,
  AdminModuleAgentConcernsResponse,
} from '@/api/generated/types.gen';

export const Route = createFileRoute('/admin/agent/concerns')({
  component: AgentConcerns,
});

const STATUSES = ['Open', 'Resolved', 'Dismissed'] as const;
type Status = (typeof STATUSES)[number];

function AgentConcerns() {
  const [status, setStatus] = useState<Status>('Open');
  const [kind, setKind] = useState<string>('concern');

  const queryString = useMemo(() => {
    const p = new URLSearchParams({ pageNumber: '1', pageSize: '100' });
    p.set('status', status);
    if (kind) p.set('kind', kind);
    return p.toString();
  }, [status, kind]);

  const concerns = useQuery({
    queryKey: ['admin', 'agent', 'concerns', queryString],
    queryFn: () =>
      api<AdminModuleAgentConcernsResponse>(`/admin/agent/concerns?${queryString}`),
    retry: false,
  });
  const items: AgentNote[] = concerns.data?.items ?? [];

  // Notes are reviewed per taxpayer, not as a flat stream — several notes on one return are one
  // piece of work, and reading them interleaved with other people's makes that impossible to see.
  // Groups keep the server's ordering (newest first) by taking each return's first appearance.
  const groups = useMemo<ConcernGroup[]>(() => {
    const byReturn = new Map<number, ConcernGroup>();
    for (const note of items) {
      const id = Number(note.taxReturnId);
      let group = byReturn.get(id);
      if (!group) {
        group = {
          taxReturnId: id,
          taxpayerName: note.taxpayerName ?? `Return #${id}`,
          year: Number(note.year ?? 0),
          notes: [],
        };
        byReturn.set(id, group);
      }
      group.notes.push(note);
    }
    return [...byReturn.values()];
  }, [items]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="section-ref">Status</span>
        <div className="flex gap-1.5">
          {STATUSES.map((s) => (
            <FilterChip
              key={s}
              label={s}
              active={status === s}
              onClick={() => setStatus(s)}
              className="capitalize"
            />
          ))}
        </div>
        <span className="ml-4 section-ref">Kind</span>
        <div className="flex gap-1.5">
          {['concern', 'observation', 'decision', 'nudge', 'followup'].map((k) => (
            <FilterChip
              key={k}
              label={k}
              active={kind === k}
              onClick={() => setKind((prev) => (prev === k ? '' : k))}
              className="capitalize"
            />
          ))}
        </div>
      </div>

      {concerns.isLoading ? (
        <Panel><Empty>Loading concerns…</Empty></Panel>
      ) : concerns.isError ? (
        <Panel><Empty>Could not load agent concerns.</Empty></Panel>
      ) : groups.length === 0 ? (
        <Panel><Empty>No notes match the current filters.</Empty></Panel>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <ReturnGroup key={g.taxReturnId} group={g} />
          ))}
        </div>
      )}

      {concerns.data && (
        <p className="text-[12px] text-(--color-ink-muted)">
          Showing {items.length} of {Number(concerns.data.totalCount ?? 0)} across{' '}
          {groups.length} return{groups.length === 1 ? '' : 's'}.
        </p>
      )}
    </div>
  );
}

type ConcernGroup = {
  taxReturnId: number;
  taxpayerName: string;
  year: number;
  notes: AgentNote[];
};

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-(--radius) border border-(--color-rule) bg-(--color-card)">
      {children}
    </div>
  );
}

/**
 * One return's notes under a header identifying whose they are. Deliberately minimal — name, tax
 * year, and how many notes — because this is a triage list, not a client record; anything more
 * belongs on the return itself, one click away.
 */
function ReturnGroup({ group }: { group: ConcernGroup }) {
  const openCount = group.notes.filter((n) => (n.status ?? 'Open') === 'Open').length;

  return (
    <Panel>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-(--color-rule) bg-(--color-paper-2) px-5 py-3">
        <Link
          to="/admin/returns/$id"
          params={{ id: String(group.taxReturnId) }}
          className="gov-link text-[14px] font-semibold"
        >
          {group.taxpayerName}
        </Link>
        {group.year > 0 && (
          <span className="font-mono text-[12px] tabular-nums text-(--color-ink-muted)">
            FY {taxYearLabel(group.year)}
          </span>
        )}
        <span className="font-mono text-[11px] tabular-nums text-(--color-ink-subtle)">
          #{group.taxReturnId}
        </span>
        <span className="ml-auto text-[12px] text-(--color-ink-muted)">
          {group.notes.length} note{group.notes.length === 1 ? '' : 's'}
          {openCount > 0 && openCount !== group.notes.length && ` · ${openCount} open`}
        </span>
      </div>
      <ul className="divide-y divide-(--color-rule)">
        {group.notes.map((n) => (
          <ConcernRow key={String(n.id)} note={n} />
        ))}
      </ul>
    </Panel>
  );
}

function ConcernRow({ note }: { note: AgentNote }) {
  const [open, setOpen] = useState(false);
  const hasEvidence = !!note.evidenceJson;
  const status = note.status ?? 'Open';
  const resolvedAt = note.resolvedAt as string | null | undefined;
  return (
    <li>
      <button
        type="button"
        onClick={() => hasEvidence && setOpen((v) => !v)}
        className={cn(
          'flex w-full items-start gap-3 px-5 py-4 text-left',
          hasEvidence ? 'cursor-pointer hover:bg-(--color-paper-2)' : 'cursor-default',
        )}
      >
        <div className="mt-0.5">
          {hasEvidence ? (
            open ? (
              <ChevronDown className="size-4 text-(--color-ink-muted)" />
            ) : (
              <ChevronRight className="size-4 text-(--color-ink-muted)" />
            )
          ) : (
            <span className="block size-4" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={statusTone(status)}>{status}</StatusPill>
            <StatusPill tone="neutral">{note.kind ?? ''}</StatusPill>
            {/* The return link lives in the group header now — repeating it on every row was
                noise once the rows are already grouped under it. */}
            <span className="ml-auto font-mono text-[11px] tabular-nums text-(--color-ink-muted)">
              {new Date(note.created).toLocaleString('en-AU')}
            </span>
          </div>
          <div className="mt-2 text-[13px] leading-relaxed text-(--color-ink)">
            {note.content ?? ''}
          </div>
          {note.resolutionNote && (
            <div className="mt-2 rounded-(--radius-sm) border-l-2 border-(--color-forest) bg-(--color-forest-soft) px-3 py-2 text-[12px] text-(--color-ink-2)">
              <span className="font-semibold text-(--color-forest)">Resolution:</span>{' '}
              {note.resolutionNote}
              {resolvedAt && (
                <span className="ml-2 font-mono text-(--color-ink-muted)">
                  {new Date(resolvedAt).toLocaleDateString('en-AU')}
                </span>
              )}
            </div>
          )}
          {open && hasEvidence && (
            <pre className="mt-3 overflow-x-auto rounded-(--radius-sm) bg-(--color-paper-2) p-3 font-mono text-[11px] leading-relaxed text-(--color-ink-2)">
              {prettyJson(note.evidenceJson ?? null)}
            </pre>
          )}
        </div>
      </button>
    </li>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-5 py-10 text-center text-[13px] text-(--color-ink-muted)">{children}</p>
  );
}

function statusTone(status: string): 'warning' | 'success' | 'neutral' {
  switch (status) {
    case 'Open':
      return 'warning';
    case 'Resolved':
      return 'success';
    default:
      return 'neutral';
  }
}

function prettyJson(raw: string | null): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
